/**
 * Audio issue detection logic for dark launch logging.
 * Determines whether the audio issue error modal would be triggered
 * and provides detailed metadata for logging.
 */

import { logSessionInfo } from '../../services/logger';
import { 
  AUDIO_ISSUE_THETA_THRESHOLDS, 
  AUDIO_ISSUE_EXCLUDED_STORY_TYPES 
} from './audioIssueDetectionConstants';

/**
 * Checks if all transcripts in the timings array are invalid.
 * A transcript is invalid if it contains only <UNK> tokens and/or whitespace.
 * @param {Array} timingsArray - Array of timing objects with transcript field
 * @returns {boolean} - True if all transcripts are invalid
 */
export function hasOnlyInvalidTranscripts(timingsArray) {
  if (!timingsArray || timingsArray.length === 0) {
    return false;
  }
  
  for (const timing of timingsArray) {
    if (timing.transcript) {
      // Remove all <UNK> tokens and whitespace, check if anything remains
      const cleaned = timing.transcript.replace(/<UNK>/gi, '').trim();
      if (cleaned.length > 0) {
        return false; // Found a valid transcript
      }
    }
  }
  return true;
}

/**
 * Processes potential audio issues and logs the result.
 * This is a dark launch feature - it only logs whether the modal would trigger,
 * without actually showing the modal.
 * 
 * @param {Object} options - Detection options
 * @param {boolean} options.isLowVolume - Whether low volume was detected
 * @param {Array} options.timings - Array of timing objects with transcript field
 * @param {Object} options.story - Story object with type and nonAudio properties
 * @param {number|undefined} options.userTheta - User's theta value
 * @param {Object|null} options.activityFlags - Activity flags from speech service
 * @param {boolean} options.forceAllUnks - Debug override to force isInvalidTranscript to true
 * @returns {Object} - Result with triggered boolean, conditionMetadata, and failureReasons
 */
export function processPotentialAudioIssues({ isLowVolume, isStaticLowBand, isStaticBroadband, isClipped, timings, story, userTheta, activityAudioMetrics, forceAllUnks }) {
  let isInvalidTranscript = hasOnlyInvalidTranscripts(timings);
  if (forceAllUnks) {
    isInvalidTranscript = true;
  }
  let isNonAudioStory = story.nonAudio;
  let isExcludedStoryType = AUDIO_ISSUE_EXCLUDED_STORY_TYPES.includes(story.type);
  let isEligibleStoryType = !isNonAudioStory && !isExcludedStoryType;
  let thetaThreshold = AUDIO_ISSUE_THETA_THRESHOLDS[story.type];
  let isAboveTheta = userTheta !== undefined && 
                       thetaThreshold !== undefined && 
                       userTheta > thetaThreshold;

  const conditionMetadata = {
    isLowVolume: isLowVolume,
    isStaticLowBand: isStaticLowBand,
    isStaticBroadband: isStaticBroadband,
    isClipped: isClipped,
    invalidTranscript: isInvalidTranscript,
    storyType: story.type,
    isNonAudioStory: isNonAudioStory,
    isExcludedStoryType: isExcludedStoryType,
    eligibleStoryType: isEligibleStoryType,
    userTheta: userTheta,
    thetaThreshold: thetaThreshold,
    isAboveTheta: isAboveTheta,
    activityAudioMetrics,
  };

  // Log reason for each condition failure
  const conditionFailureReasons = [];
  if (!isLowVolume) {
    conditionFailureReasons.push('isLowVolume: false (volume was adequate)');
  }
  if (!isStaticLowBand) {
    conditionFailureReasons.push('isStaticLowBand: false (static low band was not detected)');
  }
  if (!isStaticBroadband) {
    conditionFailureReasons.push('isStaticBroadband: false (static broadband was not detected)');
  }
  if (!isClipped) {
    conditionFailureReasons.push('isClipped: false (clipped was not detected)');
  }
  if (!isInvalidTranscript) {
    conditionFailureReasons.push('invalidTranscript: false (found valid transcripts)');
  }
  if (!isEligibleStoryType) {
    if (isNonAudioStory) {
      conditionFailureReasons.push('eligibleStoryType: false (story is nonAudio)');
    } else if (isExcludedStoryType) {
      conditionFailureReasons.push(`eligibleStoryType: false (story type '${story.type}' is excluded)`);
    }
  }
  if (!isAboveTheta) {
    if (userTheta === undefined) {
      conditionFailureReasons.push('isAboveTheta: false (userTheta is undefined)');
    } else if (thetaThreshold === undefined) {
      conditionFailureReasons.push(`isAboveTheta: false (no theta threshold defined for story type '${story.type}')`);
    } else {
      conditionFailureReasons.push(`isAboveTheta: false (userTheta ${userTheta} <= threshold ${thetaThreshold})`);
    }
  }

  const audioIssueDetectorTriggered = isLowVolume || isStaticLowBand || isStaticBroadband || isClipped;

  const triggered = audioIssueDetectorTriggered && isInvalidTranscript && isEligibleStoryType && isAboveTheta;

  if (triggered) {
    // All conditions met - would have triggered error modal
    logSessionInfo('audio_issue_detection_error_triggered', {
      ...conditionMetadata,
      triggered: true,
    });
  } else {
    // Conditions not met - log which ones failed
    logSessionInfo('audio_issue_detection_error_not_triggered', {
      ...conditionMetadata,
      triggered: false,
      conditionFailureReasons,
    });
  }

  return { triggered, conditionMetadata, conditionFailureReasons };
}
